import * as React from "react"

import { cn } from "../../lib/utils"
import { buttonVariants, type ButtonVariantProps } from "./button-variants"
import { IconChevronLeft, IconChevronRight, IconDots } from "@tabler/icons-react"

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      aria-label="pagination"
      role="navigation"
      data-slot="pagination"
      className={cn("[margin-inline:auto] [display:flex] [width:100%] [justify-content:center]", className)}
      {...props}
    />
  )
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("[display:flex] [align-items:center] [gap:0.125rem] [margin:0rem] [padding:0rem] [list-style:none]", className)}
      {...props}
    />
  )
}

function PaginationItem({ className, ...props }: React.ComponentProps<"li">) {
  return <li data-slot="pagination-item" className={cn("[list-style:none]", className)} {...props} />
}

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonVariantProps, "size"> &
  React.ComponentProps<"a">

function PaginationLink({
  className,
  isActive,
  size = "icon",
  children,
  ...props
}: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? "page" : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        buttonVariants({ variant: isActive ? "outline" : "ghost", size }),
        "[text-decoration:none]",
        isActive && "[cursor:default] [pointer-events:none] hover:[background-image:none] hover:[background-color:var(--color-bg-elevated-transparent)] hover:[color:var(--color-text-primary)] [&[data-pressed]]:[background-image:none] [&[data-pressed]]:[background-color:var(--color-bg-elevated-transparent)] [&[data-pressed]]:[color:var(--color-text-primary)]",
        className
      )}
      {...props}
    >
      {children}
    </a>
  )
}

function PaginationPrevious({
  className,
  text = "Previous",
  ...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn("[padding-left:0.375rem_!important]", className)}
      {...props}
    >
      <IconChevronLeft data-icon="inline-start" />
      <span className={"[display:none] sm:[display:block]"}>{text}</span>
    </PaginationLink>
  )
}

function PaginationNext({
  className,
  text = "Next",
  ...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn("[padding-right:0.375rem_!important]", className)}
      {...props}
    >
      <span className={"[display:none] sm:[display:block]"}>{text}</span>
      <IconChevronRight data-icon="inline-end" />
    </PaginationLink>
  )
}

function PaginationEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn(
        "[display:flex] [width:2rem] [height:2rem] [align-items:center] [justify-content:center] [&_svg:not([class*='size-'])]:[width:1rem] [&_svg:not([class*='size-'])]:[height:1rem]",
        className
      )}
      {...props}
    >
      <IconDots
      />
      <span className={"sr-only"}>More pages</span>
    </span>
  )
}

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
